import { useState } from "react";
import { Link } from "react-router-dom";
import { formatDateRange, getTripStatus, type TripSummary } from "@travelix/core";
import { cn } from "@/lib/utils";
import { isLikelyBrokenCoverUrl } from "@/lib/cover-url";
import { useTripCoverFallback } from "@/hooks/use-trip";

interface TripCardProps {
  trip: TripSummary;
  className?: string;
}

/**
 * Photo-forward trip card — the dashboard signature component.
 * The image is a CSS gradient stand-in when trip.miniature is null;
 * production loads real Unsplash / user-uploaded covers.
 */
export function TripCard({ trip, className }: TripCardProps) {
  const status = getTripStatus(trip);
  const dateRange = formatDateRange(trip.startDate, trip.endDate);

  // Bad URLs come from v1 leftovers (deprecated source.unsplash.com), from
  // empty strings, or from any 404 at runtime. All three fall through to
  // the gradient stand-in.
  const [broken, setBroken] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const summaryUsable =
    !!trip.miniature && !isLikelyBrokenCoverUrl(trip.miniature);

  // When the embedded summary is missing or stale (see D-015: no photo
  // write path, so coverImage upgraded out-of-band never cascades),
  // fall back to reading trip.coverImage. useTripCoverFallback only fires
  // the read when summaryUsable is false, and shares its cache with useTrip.
  const { data: fallbackCover } = useTripCoverFallback(trip.id, !summaryUsable);
  const fallbackUsable =
    !!fallbackCover && !isLikelyBrokenCoverUrl(fallbackCover);

  const src = summaryUsable ? trip.miniature : fallbackUsable ? fallbackCover : null;
  const showImage = !!src && !broken;

  const statusStyle: Record<typeof status, string> = {
    upcoming: "bg-primary/90 text-white",
    active: "bg-success/90 text-white",
    past: "bg-white/20 text-white border border-white/30",
  };

  return (
    <Link
      to={`/trip/${trip.id}`}
      aria-label={`${trip.name}, ${dateRange}, ${status}`}
      className={cn(
        "group relative block overflow-hidden rounded-lg shadow-warm-2",
        "aspect-[5/3] transition-transform duration-200 active:scale-[0.99]",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
        className,
      )}
      style={{ viewTransitionName: `trip-${trip.id}-image` }}
    >
      {/* Gradient stand-in is always rendered underneath — it's the safe fallback
          when the image is missing, blocked, or fails to load. */}
      <div
        className="absolute inset-0 transition-transform duration-500 group-hover:scale-[1.03]"
        style={{ background: gradientForCity(trip.city) }}
        aria-hidden
      />
      {showImage ? (
        <img
          src={src!}
          alt=""
          onError={() => setBroken(true)}
          onLoad={() => setLoaded(true)}
          loading="lazy"
          decoding="async"
          className={cn(
            "absolute inset-0 h-full w-full object-cover transition-[filter,opacity,transform] duration-500 group-hover:scale-[1.03]",
            loaded ? "opacity-100 blur-0" : "opacity-0 blur-md",
          )}
        />
      ) : null}

      {/* Legibility gradient */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to top, rgba(20,15,10,0.78) 0%, rgba(20,15,10,0) 58%)",
        }}
        aria-hidden
      />

      {/* Status pill */}
      <span
        className={cn(
          "absolute top-3 right-3 rounded-full px-2.5 py-1 backdrop-blur-md",
          "text-[10px] font-bold uppercase tracking-[0.1em]",
          statusStyle[status],
        )}
      >
        {status}
      </span>

      {/* Text overlay */}
      <div className="absolute inset-x-4 bottom-3.5 flex flex-col gap-0.5 text-white">
        <h3
          className="font-display leading-[1.05] text-white m-0"
          style={{ fontVariationSettings: `"opsz" 48, "wght" 500`, fontSize: 22, letterSpacing: "-0.01em" }}
        >
          {trip.name}
        </h3>
        <p className="text-[12px] font-medium text-white/85 m-0 tracking-wide">
          {dateRange} · {trip.city}
        </p>
      </div>
    </Link>
  );
}

/**
 * Fake photo palette per destination — deterministic gradient stand-ins
 * so covers look "assigned" rather than random. Production replaces with real photos.
 */
function gradientForCity(city: string): string {
  const hash = [...city.toLowerCase()].reduce((a, c) => a + c.charCodeAt(0), 0);
  const gradients = [
    "linear-gradient(135deg, #E4572E 0%, #D08A3F 40%, #B87E28 70%, #7A2712 100%)", // sunset
    "linear-gradient(150deg, #A9C4D6 0%, #6E8FA8 40%, #4A6272 75%, #2A3A47 100%)", // alps
    "linear-gradient(160deg, #F4C89D 0%, #E8A972 30%, #C97B3F 65%, #7A4A24 100%)", // lisbon
    "linear-gradient(155deg, #F3D7CF 0%, #E4572E 45%, #7A2712 100%)", // tokyo
    "linear-gradient(140deg, #DDEBE1 0%, #7BB799 45%, #3F7A5E 100%)", // forest
  ];
  return gradients[hash % gradients.length]!;
}
